<script lang="ts">
import { defineComponent, computed, type PropType } from 'vue';
import type { UIGameOverState } from '@legendary-arena/game-engine';
// why: WP-583 — gradeForFinalScore is a runtime VALUE, so it needs its own value
// import; the `import type` above cannot carry it. The engine bands the number;
// the player-facing word comes from the client `gradeDisplay` helper (D-24392).
import { gradeForFinalScore } from '@legendary-arena/game-engine';
import { gradeLabel, gradeClass, gradeAriaText, buildGradeScale } from '../../vfx/gradeDisplay';
import { buildWorkedScoreCalc, buildLuckRead, buildScoringKey, playerLabel } from '../../vfx/scoreCalcDisplay';
import type { MyCompetitiveScore, CompetitiveSeatIdentity } from '../../lib/api/competitionApi';
import EndgameCoachPanel from './EndgameCoachPanel.vue';

/**
 * Builds the human-readable "defeated" phrases for one seat's contribution row
 * (WP-621) — one phrase per non-null, positive count, correctly singularised.
 * Returns an empty array when the record predates WP-616 (all counts null) or
 * the seat defeated nothing, so the report card omits the line entirely rather
 * than printing a row of zeros for a seat that was simply never counted.
 *
 * @param row - One per-player row from `workedCalc.perPlayer`.
 * @returns Zero to three phrases like "3 villains", "1 henchman".
 */
function contributionPhrases(row: {
  readonly villainsDefeated: number | null;
  readonly henchmenDefeated: number | null;
  readonly mastermindTacticsDefeated: number | null;
}): string[] {
  const phrases: string[] = [];
  if (row.villainsDefeated !== null && row.villainsDefeated > 0) {
    const noun = row.villainsDefeated === 1 ? 'villain' : 'villains';
    phrases.push(`${row.villainsDefeated} ${noun}`);
  }
  if (row.henchmenDefeated !== null && row.henchmenDefeated > 0) {
    const noun = row.henchmenDefeated === 1 ? 'henchman' : 'henchmen';
    phrases.push(`${row.henchmenDefeated} ${noun}`);
  }
  if (row.mastermindTacticsDefeated !== null && row.mastermindTacticsDefeated > 0) {
    const noun = row.mastermindTacticsDefeated === 1 ? 'tactic' : 'tactics';
    phrases.push(`${row.mastermindTacticsDefeated} mastermind ${noun}`);
  }
  return phrases;
}

/**
 * Builds this seat's celebratory synergy line for the report card (WP-708 /
 * D-24531) — "assembled N of M synergy clauses" — or null when the seat played no
 * conditional clause (nothing to show) or the record predates WP-708. The surface
 * celebrates what was assembled and never says "whiff", "failed", or "missed".
 *
 * @param row - One per-player row from `workedCalc.perPlayer`.
 * @returns The synergy phrase, or null to omit the line.
 */
function synergyPhrase(row: {
  readonly conditionalClausesPlayed: number | null;
  readonly conditionalClausesAssembled: number | null;
}): string | null {
  if (row.conditionalClausesPlayed === null || row.conditionalClausesPlayed <= 0) {
    return null;
  }
  const assembled = row.conditionalClausesAssembled ?? 0;
  const noun = row.conditionalClausesPlayed === 1 ? 'synergy clause' : 'synergy clauses';
  return `assembled ${assembled} of ${row.conditionalClausesPlayed} ${noun}`;
}

/**
 * Builds this seat's celebratory Realized Value % line for the report card
 * (WP-709 / D-24532) — "realized N% of synergy value" — or null when the seat's
 * conditional clauses offered no attack/recruit value (nothing to weigh) or the
 * record predates WP-709. Value-weights the synergy so landing a big swing reads
 * higher than a chip; the surface never says "whiff", "failed", or "missed".
 *
 * @param row - One per-player row from `workedCalc.perPlayer`.
 * @returns The Realized Value % phrase, or null to omit the line.
 */
function realizedValuePhrase(row: {
  readonly conditionalClausesPotentialValue: number | null;
  readonly conditionalClausesRealizedValue: number | null;
}): string | null {
  const potential = row.conditionalClausesPotentialValue;
  // why: WP-709 — no line when the seat's clauses offered no value (guard the
  // divide-by-zero); nothing was set up to realize, so there is no percentage.
  if (potential === null || potential <= 0) {
    return null;
  }
  const realized = row.conditionalClausesRealizedValue ?? 0;
  const percent = Math.round((100 * realized) / potential);
  return `realized ${percent}% of synergy value`;
}

/**
 * The per-seat Synergy Realization figures projected onto `gameOver.synergyContributions`
 * (WP-715 / D-24538). Shape mirrors the engine's `conditionalClauses` entry.
 */
interface CoopSynergyEntry {
  readonly played: number;
  readonly assembled: number;
  readonly potentialValue: number;
  readonly realizedValue: number;
}

/**
 * Casual/co-op recap synergy line — delegates to `synergyPhrase` so the wording is
 * byte-identical to the ranked report card (WP-715). Reads the always-available
 * `gameOver.synergyContributions` entry instead of a `workedCalc` row, so a casual
 * (`par_not_published`) match shows synergy too.
 *
 * @param entry - This seat's synergy contribution, or undefined if none.
 * @returns The synergy phrase, or null to omit the line.
 */
function coopSynergyPhrase(entry: CoopSynergyEntry | undefined): string | null {
  if (entry === undefined) {
    return null;
  }
  return synergyPhrase({
    conditionalClausesPlayed: entry.played,
    conditionalClausesAssembled: entry.assembled,
  });
}

/**
 * Casual/co-op recap Realized Value % line — delegates to `realizedValuePhrase` for
 * identical wording (WP-715), reading `gameOver.synergyContributions`.
 *
 * @param entry - This seat's synergy contribution, or undefined if none.
 * @returns The Realized Value % phrase, or null to omit the line.
 */
function coopRealizedValuePhrase(entry: CoopSynergyEntry | undefined): string | null {
  if (entry === undefined) {
    return null;
  }
  return realizedValuePhrase({
    conditionalClausesPotentialValue: entry.potentialValue,
    conditionalClausesRealizedValue: entry.realizedValue,
  });
}

// why: the four literal leaf-name `aria-label`s on the PAR breakdown
// (`rawScore`, `parScore`, `finalScore`, `scoringConfigVersion`) bind the
// HUD directly to the WP-067 drift test at
// `packages/game-engine/src/ui/uiState.types.drift.test.ts`. A rename of
// any of those fields must break the drift test AND this component's test
// in lockstep, preventing silent screen-reader regressions. The `par`
// block is absent on every runtime UIState today under D-6701 —
// `EndgameSummary` renders the outcome / reason / scores portion and omits
// the par block entirely until the payload-wiring WP lands.
// why: defineComponent form (not <script setup>) is required here because
// the template references computed bindings (`hasPar`, `hasScores`) that
// must be returned from `setup()` to reach `_ctx` under vue-sfc-loader's
// separate-compile pipeline (D-6512 / P6-30).
export default defineComponent({
  name: 'EndgameSummary',
  // why: WP-595 — the Legendary-Pass AI coach panel, rendered inside the
  // competitive-score section when a scored record with a replay hash exists, and
  // (WP-752) after the recap for a casual match keyed by its match id.
  components: { EndgameCoachPanel },
  props: {
    gameOver: {
      type: Object as PropType<UIGameOverState>,
      required: true,
    },
    // why: WP-578 — the server-computed competitive score for this match,
    // threaded from `useCompetitiveSubmitOnGameover` (PlayViewport). Optional:
    // `null` for guests, pending/failed submits, and non-scoring matches, in
    // which case the panel renders the outcome + VP summary unchanged.
    competitiveScore: {
      type: Object as PropType<MyCompetitiveScore | null>,
      default: null,
    },
    // why: when the viewer played this match as a GUEST (no account), the
    // competitive-score section is absent (competitiveScore is null). Rather than
    // leave that slot empty, show a sign-in conversion prompt. Forward-looking on
    // purpose: a guest cannot retroactively save THIS match (seat→account
    // ownership is fixed at join time, D-24119), so the CTA sells ranking future
    // matches, not saving this one. Threaded from PlayViewport (submissionStatus
    // === 'guest').
    showGuestSignIn: {
      type: Boolean,
      default: false,
    },
    // why: D-24630 — the viewer WAS signed in, but the sign-in expired before
    // gameover. The seat is still owned by the account bound at join (D-24119), so
    // unlike a guest this match CAN be saved: signing back in returns to the match
    // and submits it. Threaded from PlayViewport (submissionStatus ===
    // 'session-expired').
    showSessionExpiredSignIn: {
      type: Boolean,
      default: false,
    },
    // why: INFRA (endgame seat names) — the per-seat identity roster
    // ({ playerId, isBot, handle }) fetched on gameover (PlayViewport →
    // useSeatIdentitiesOnGameover), so the co-op VP recap labels seats
    // "Player N (@handle)" / "(Bot)" — the same labelling the competitive block
    // gets from competitiveScore.seatIdentities. Null until the fetch resolves and
    // for a failed/unavailable read; the recap then falls back to plain "Player N".
    seatIdentities: {
      type: Array as PropType<readonly CompetitiveSeatIdentity[] | null>,
      default: null,
    },
    // why: WP-752 / D-24576 — the match id to coach when this match is unscored
    // (computed once in PlayViewport by computeCasualCoachMatchId: signed in,
    // submit settled as `ineligible`). Null for scored, guest, pending, failed and
    // ended-early matches, so the casual coach panel renders only when it is set.
    casualCoachMatchId: {
      type: String as () => string | null,
      default: null,
    },
  },
  setup(props) {
    // Guarded accessors for optional fields — fail-soft-for-optional per
    // the session prompt's failure-semantics rule.
    const hasPar = computed(() => 'par' in props.gameOver);
    const hasScores = computed(() => 'scores' in props.gameOver);

    // why: WP-583 — the grade needs only the always-present finalScore, so the
    // badge shows whenever a competitive score exists; the component breakdown
    // needs the optional scoreBreakdown and is gated separately below.
    const grade = computed(() =>
      props.competitiveScore ? gradeForFinalScore(props.competitiveScore.finalScore) : null,
    );
    const gradeBadgeLabel = computed(() => (grade.value ? gradeLabel(grade.value) : ''));
    const gradeBadgeClass = computed(() => (grade.value ? gradeClass(grade.value) : ''));
    const gradeBadgeAria = computed(() => (grade.value ? gradeAriaText(grade.value) : ''));

    // why: the full component breakdown the server already returned (rendered
    // verbatim; never recomputed client-side — WP-578 / D-24387). Null for an
    // older record that carries no breakdown; the headline still renders.
    const breakdown = computed(() => props.competitiveScore?.scoreBreakdown ?? null);

    // why: WP-584 — render the score as a worked solution (formula → substituted
    // → products → result), not a flat list. The per-term weights are DERIVED
    // from the breakdown (product ÷ count) inside the helper, never hardcoded, so
    // the shown formula cannot drift from the engine's real weights (D-24393).
    const workedCalc = computed(() =>
      breakdown.value
        ? buildWorkedScoreCalc(breakdown.value, props.competitiveScore?.seatIdentities)
        : null,
    );

    // why: WP-593 — the objective luck-of-the-draw read (favorable / even /
    // difficult shuffle), computed deterministically from the breakdown's actual
    // adversity vs the scenario's PAR expectation. Absent for older records with no
    // WP-591 adversity baseline; the opinionated hero/purchase coaching is a
    // separate Pass-gated LLM feature (WP-B), not built here.
    const luckRead = computed(() =>
      breakdown.value ? buildLuckRead(breakdown.value) ?? null : null,
    );

    // why: WP-587 — the full grade scale (every band, its final-score range, and a
    // "you are here" marker) so the player sees what a B / A / Legendary needs, not
    // just their own grade. The engine ships the numeric bands (SCORE_GRADE_BANDS);
    // buildGradeScale owns the words + range formatting (D-24396 / D-24392 boundary).
    const gradeScale = computed(() => (grade.value ? buildGradeScale(grade.value) : null));

    // why: WP-600 / D-24410 — the scoring key (what every event is worth), grouped into
    // awards vs penalties, shown beside the grade scale so a player can read the whole
    // scoring system at a glance. Static rulebook-faithful weights (buildScoringKey owns
    // them); rendered whenever a competitive score exists.
    const scoringKey = buildScoringKey();

    // why: INFRA (endgame seat names) — index the roster once so the co-op recap
    // can label each seat exactly as the competitive per-player block does
    // (`playerLabel`): "Player N", "Player N (Bot)", or "Player N (@handle)".
    const identityByPlayer = computed(() => {
      const map = new Map<string, CompetitiveSeatIdentity>();
      for (const identity of props.seatIdentities ?? []) {
        map.set(identity.playerId, identity);
      }
      return map;
    });

    /**
     * Label one co-op recap seat. Reuses `playerLabel` so the wording matches the
     * competitive block; a seat with no identity (roster absent, or a guest /
     * handle-less seat) falls back to a plain "Player N".
     *
     * @param playerId - The boardgame.io seat id ("0", "1", ...).
     * @returns The seat label.
     */
    function seatLabel(playerId: string): string {
      return playerLabel(playerId, identityByPlayer.value.get(playerId));
    }

    // why: WP-708 / D-24531 — the neutral cross-seat Table Total (sum of every seat's
    // assembled clauses), shown as a celebration headline above the per-seat rows.
    // Null when no seat carries synergy data (records predating WP-708), so the
    // headline is omitted rather than showing "0".
    const synergyTableTotal = computed<number | null>(() => {
      const rows = workedCalc.value?.perPlayer;
      if (rows === undefined) {
        return null;
      }
      let total = 0;
      let anySynergy = false;
      for (const row of rows) {
        if (row.conditionalClausesPlayed !== null && row.conditionalClausesPlayed > 0) {
          anySynergy = true;
          total = total + (row.conditionalClausesAssembled ?? 0);
        }
      }
      return anySynergy ? total : null;
    });

    // why: WP-715 / D-24538 — the casual/co-op recap's cross-seat Table Total, summed
    // from the always-available `gameOver.synergyContributions` (not the ranked
    // `workedCalc`), so a `par_not_published` match shows the headline too. Null when no
    // seat carries synergy data (pre-WP-708 records / nothing played), matching the
    // ranked `synergyTableTotal` behaviour.
    const coopSynergyTableTotal = computed<number | null>(() => {
      const contributions = props.gameOver.synergyContributions;
      if (contributions === undefined) {
        return null;
      }
      let total = 0;
      let anySynergy = false;
      for (const entry of Object.values(contributions)) {
        if (entry.played > 0) {
          anySynergy = true;
          total = total + entry.assembled;
        }
      }
      return anySynergy ? total : null;
    });

    return {
      hasPar,
      hasScores,
      gradeBadgeLabel,
      gradeBadgeClass,
      gradeBadgeAria,
      workedCalc,
      luckRead,
      gradeScale,
      scoringKey,
      contributionPhrases,
      synergyPhrase,
      realizedValuePhrase,
      synergyTableTotal,
      coopSynergyPhrase,
      coopRealizedValuePhrase,
      coopSynergyTableTotal,
      seatLabel,
    };
  },
});
</script>

<template>
  <section
    class="endgame-summary"
    data-testid="arena-hud-endgame"
    aria-label="endgame summary"
  >
    <header>
      <span class="outcome" aria-label="outcome">
        Outcome: {{ gameOver.outcome }}
      </span>
      <span class="reason" aria-label="reason">
        {{ gameOver.reason }}
      </span>
    </header>

    <!-- why: WP-578 — the competitive score the server computed for this match,
         shown to the player who earned it. `finalScore` is the ranked, PAR-
         relative, lower-is-better value; `rawScore` is supporting detail. Both
         are rendered verbatim (never recomputed client-side). Absent for guests,
         pending/failed submits, and non-scoring matches (prop is null). -->
    <section
      v-if="competitiveScore"
      class="competitive-score"
      data-testid="arena-hud-competitive-score"
      aria-label="competitive score"
    >
      <p class="competitive-score-headline">
        Competitive score:
        <strong aria-label="competitiveFinalScore">{{ competitiveScore.finalScore }}</strong>
        <span class="competitive-score-hint">(lower is better)</span>
        <!-- why: WP-583 — the grade badge conveys meaning by its TEXT label (not
             colour alone) and carries an aria-label, so it is legible to a screen
             reader; the class only tints it. -->
        <span
          class="grade-badge"
          :class="gradeBadgeClass"
          :aria-label="gradeBadgeAria"
          data-testid="arena-hud-grade-badge"
        >{{ gradeBadgeLabel }}</span>
      </p>
      <!-- why: WP-593 — the raw score as a two-sided ledger: penalties that raised
           it (worse) and rewards that lowered it (better), netting to the raw score.
           A restyle of the same values the worked formula below shows (never
           recomputed). Falls back to the plain figure for a record with no
           breakdown (rawLedger comes from the breakdown). -->
      <div
        v-if="workedCalc"
        class="raw-ledger"
        data-testid="arena-hud-raw-ledger"
        aria-label="raw score ledger"
      >
        <div class="raw-ledger-side raw-ledger-penalties">
          <div class="raw-ledger-heading">Penalties <span class="raw-ledger-sign">(raise score)</span></div>
          <div v-if="workedCalc.rawLedger.penalties.length === 0" class="raw-ledger-row raw-ledger-none">
            <span class="raw-ledger-label">None</span>
            <span class="raw-ledger-amount">0</span>
          </div>
          <div v-for="line in workedCalc.rawLedger.penalties" :key="line.label" class="raw-ledger-row">
            <span class="raw-ledger-label">{{ line.label }}</span>
            <span class="raw-ledger-amount">+{{ line.amount }}</span>
          </div>
          <div class="raw-ledger-row raw-ledger-subtotal">
            <span class="raw-ledger-label">Subtotal</span>
            <span class="raw-ledger-amount">+{{ workedCalc.rawLedger.penaltyTotal }}</span>
          </div>
        </div>
        <div class="raw-ledger-side raw-ledger-earned">
          <div class="raw-ledger-heading">Earned <span class="raw-ledger-sign">(lower score)</span></div>
          <div v-if="workedCalc.rawLedger.earned.length === 0" class="raw-ledger-row raw-ledger-none">
            <span class="raw-ledger-label">None</span>
            <span class="raw-ledger-amount">0</span>
          </div>
          <div v-for="line in workedCalc.rawLedger.earned" :key="line.label" class="raw-ledger-row">
            <span class="raw-ledger-label">{{ line.label }}</span>
            <span class="raw-ledger-amount">−{{ line.amount }}</span>
          </div>
          <div class="raw-ledger-row raw-ledger-subtotal">
            <span class="raw-ledger-label">Subtotal</span>
            <span class="raw-ledger-amount">−{{ workedCalc.rawLedger.earnedTotal }}</span>
          </div>
        </div>
        <div class="raw-ledger-net">
          <span class="raw-ledger-net-label">Raw score</span>
          <span class="raw-ledger-net-value" aria-label="competitiveRawScore">{{ workedCalc.rawLedger.total }}</span>
        </div>
      </div>
      <p v-else class="competitive-score-detail">
        Raw score <span aria-label="competitiveRawScore">{{ competitiveScore.rawScore }}</span>
      </p>

      <!-- why: WP-584 — the score rendered as a worked solution (formula-first:
           symbolic formula → values substituted → products → result; then
           Final = Raw − PAR). All values are from the server-returned breakdown,
           rendered verbatim (never recomputed); the per-term weights are derived
           in `scoreCalcDisplay` (D-24393). Gated on the optional breakdown so an
           older record without one still shows the headline. -->
      <div
        v-if="workedCalc"
        class="score-worked"
        data-testid="arena-hud-score-breakdown"
        aria-label="score calculation"
      >
        <div class="worked-givens" aria-label="score inputs">
          <span v-for="given in workedCalc.givens" :key="given.label" class="worked-given">
            {{ given.label }} <strong>{{ given.value }}</strong>
          </span>
        </div>

        <div class="worked-block">
          <div class="worked-heading">Raw score</div>
          <div class="worked-line" aria-label="rawFormula">Raw = {{ workedCalc.formula }}</div>
          <div class="worked-line worked-indent" aria-label="rawSubstituted">= {{ workedCalc.substituted }}</div>
          <div class="worked-line worked-indent" aria-label="rawProducts">= {{ workedCalc.products }}</div>
          <div class="worked-line worked-indent worked-result" aria-label="rawResult">= {{ workedCalc.rawScore }}</div>
        </div>

        <!-- why: WP-588 — break the team-total reward terms (Bystanders, VP) down by
             player so each seat sees their own contribution. A display-only split of
             the same totals — the per-player VP and bystanders sum to the team figures
             used in the raw calc above. Absent for records persisted before WP-588. -->
        <div v-if="workedCalc.perPlayer" class="worked-block" data-testid="arena-hud-per-player">
          <div class="worked-heading">By player</div>
          <!-- why: WP-708 — the neutral cross-seat Table Total, a celebration headline
               (sum of every seat's assembled synergy clauses). Omitted when no seat
               carries synergy data (pre-WP-708 records). Copy order (locked): table
               total first, then each seat's own rate. -->
          <div
            v-if="synergyTableTotal !== null"
            class="worked-synergy-total"
            data-testid="arena-hud-synergy-total"
          >The table assembled {{ synergyTableTotal }} synergy {{ synergyTableTotal === 1 ? 'clause' : 'clauses' }}</div>
          <div class="per-player-grid" aria-label="per-player scoring">
            <div v-for="row in workedCalc.perPlayer" :key="row.label" class="per-player-row">
              <span class="per-player-name">{{ row.label }}</span>
              <span class="per-player-stat"><strong>{{ row.victoryPoints }}</strong> VP</span>
              <span class="per-player-stat"><strong>{{ row.bystandersRescued }}</strong> bystanders rescued</span>
              <!-- why: WP-621 — this seat's team-contribution split (villains,
                   henchmen, mastermind tactics it defeated). Omitted entirely
                   when the seat defeated nothing or the record predates WP-616
                   (no per-seat counts) — never a misleading row of zeros. -->
              <span
                v-if="contributionPhrases(row).length > 0"
                class="per-player-stat per-player-contrib"
                data-testid="arena-hud-per-player-contrib"
                aria-label="defeated"
              >defeated {{ contributionPhrases(row).join(', ') }}</span>
              <!-- why: WP-708 — this seat's celebratory synergy line ("assembled N of
                   M synergy clauses"). Omitted when the seat played no conditional
                   clause or the record predates WP-708. Never "whiff/failed/missed". -->
              <span
                v-if="synergyPhrase(row) !== null"
                class="per-player-stat per-player-synergy"
                data-testid="arena-hud-per-player-synergy"
                aria-label="synergy"
              >{{ synergyPhrase(row) }}</span>
              <!-- why: WP-709 — this seat's celebratory Realized Value % line
                   ("realized N% of synergy value"), below the synergy-clause line.
                   Omitted when the seat's clauses offered no attack/recruit value or
                   the record predates WP-709. Never "whiff/failed/missed". -->
              <span
                v-if="realizedValuePhrase(row) !== null"
                class="per-player-stat per-player-realized-value"
                data-testid="arena-hud-per-player-realized-value"
                aria-label="realized value"
              >{{ realizedValuePhrase(row) }}</span>
            </div>
          </div>
        </div>

        <!-- why: WP-587 — show where PAR came from (the same formula applied to the
             scenario's expected baseline), not just the final PAR value. Absent for
             records persisted before WP-587 (no parBaseline in the stored breakdown);
             the Final block below then still shows the verbatim PAR value. -->
        <div v-if="workedCalc.parDerivation" class="worked-block" data-testid="arena-hud-par-derivation">
          <div class="worked-heading">PAR for this scenario</div>
          <div class="worked-givens" aria-label="par baseline">
            <span class="worked-given">Expected escapes <strong>{{ workedCalc.parDerivation.baseline.escapes }}</strong></span>
            <span v-if="workedCalc.parDerivation.baseline.twists > 0" class="worked-given">Expected twists <strong>{{ workedCalc.parDerivation.baseline.twists }}</strong></span>
            <span v-if="workedCalc.parDerivation.baseline.bystandersLost > 0" class="worked-given">Expected bystanders lost <strong>{{ workedCalc.parDerivation.baseline.bystandersLost }}</strong></span>
            <span class="worked-given">Expected bystanders rescued <strong>{{ workedCalc.parDerivation.baseline.bystanders }}</strong></span>
            <span class="worked-given">Expected VP <strong>{{ workedCalc.parDerivation.baseline.victoryPoints }}</strong></span>
          </div>
          <div class="worked-line" aria-label="parFormula">PAR = {{ workedCalc.parDerivation.formula }}</div>
          <div class="worked-line worked-indent" aria-label="parSubstituted">= {{ workedCalc.parDerivation.substituted }}</div>
          <div class="worked-line worked-indent worked-result" aria-label="parResult">= {{ workedCalc.parScore }}</div>
          <!-- why: WP-588 — name what sets PAR. It is keyed on the scenario identity
               (ScenarioKey = scheme::mastermind::villain-groups) and calibrated from the
               scheme, mastermind, and villain-group difficulties — NOT the henchmen. -->
          <p class="par-basis" aria-label="parBasis">
            Set by this scenario — its scheme, mastermind, and villain groups.
          </p>
        </div>

        <div class="worked-block">
          <div class="worked-heading">Final score</div>
          <div class="worked-line" aria-label="finalSubstituted">Final = Raw − PAR = {{ workedCalc.finalSubstituted }}</div>
          <div class="worked-line worked-indent worked-result" aria-label="finalResult">= {{ workedCalc.finalScore }}</div>
        </div>
      </div>

      <!-- why: WP-587 — the grade scale: every band with its final-score range and a
           marker for the grade the player earned, so "Grade A" is legible in context
           (what a B / A / Legendary needs). Shown whenever a competitive score exists
           (grade needs only finalScore); independent of the optional breakdown. The
           current row is marked by TEXT ("← your score") + aria-current, not colour
           alone. -->
      <div
        v-if="gradeScale"
        class="grade-scale"
        data-testid="arena-hud-grade-scale"
        aria-label="grade scale"
      >
        <div class="worked-heading">Grade scale (final score vs PAR — lower is better)</div>
        <!-- why: WP-588 — a horizontal, colour-coded scale (wider, not a tall list) so
             the whole ladder reads at a glance and the earned band stands out. Colour is
             REINFORCEMENT: the current cell also carries a text marker ("your score") +
             aria-current, so it is legible with colours disabled or to a screen reader. -->
        <ol class="grade-scale-strip">
          <li
            v-for="entry in gradeScale"
            :key="entry.grade"
            class="grade-scale-cell"
            :class="['grade-scale-cell--' + entry.grade, { 'grade-scale-cell--current': entry.isCurrent }]"
            :aria-current="entry.isCurrent ? 'true' : undefined"
          >
            <span class="grade-scale-label">{{ entry.label }}</span>
            <span class="grade-scale-range">{{ entry.range }}</span>
            <span v-if="entry.isCurrent" class="grade-scale-you">your score</span>
          </li>
        </ol>
      </div>

      <!-- why: WP-600 / D-24410 — the scoring key: what every event is worth, grouped
           into awards (lower your score) and penalties (raise it), so a player can read
           the whole scoring system at a glance beside their grade. Rulebook-faithful —
           a rescued bystander scores inside Victory Points (1 VP each), never as its own
           award; the moral weight is the Bystander-lost penalty. Colour reinforces the
           group (awards cool, penalties warm); each side is also labelled in text. -->
      <div
        class="scoring-key"
        data-testid="arena-hud-scoring-key"
        aria-label="scoring key"
      >
        <div class="worked-heading">Scoring key — what each event is worth</div>
        <div class="scoring-key-cols">
          <div class="scoring-key-col scoring-key-col--awards" aria-label="awards">
            <div class="scoring-key-col-heading">Awards <span class="scoring-key-sign">(lower your score)</span></div>
            <div v-for="line in scoringKey.awards" :key="line.label" class="scoring-key-row">
              <span class="scoring-key-points">{{ line.points }}</span>
              <span class="scoring-key-body">
                <span class="scoring-key-label">{{ line.label }}</span>
                <span class="scoring-key-note">{{ line.note }}</span>
              </span>
            </div>
          </div>
          <div class="scoring-key-col scoring-key-col--penalties" aria-label="penalties">
            <div class="scoring-key-col-heading">Penalties <span class="scoring-key-sign">(raise your score)</span></div>
            <div v-for="line in scoringKey.penalties" :key="line.label" class="scoring-key-row">
              <span class="scoring-key-points">{{ line.points }}</span>
              <span class="scoring-key-body">
                <span class="scoring-key-label">{{ line.label }}</span>
                <span class="scoring-key-note">{{ line.note }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- why: WP-593 — the objective "luck of the draw" read: how much adversity
           the match dealt (scheme twists / villain escapes / bystanders lost) versus
           what this scenario's PAR expects, banded into favorable / even / difficult
           and framed encouragingly. Deterministic from the breakdown (never from deck
           order, which the engine never projects). Absent for older records with no
           WP-591 adversity baseline. The opinionated hero/purchase coaching is a
           separate Pass-gated LLM feature (WP-B), not shown here. -->
      <div
        v-if="luckRead"
        class="luck-read"
        :class="'luck-read--' + luckRead.verdict"
        data-testid="arena-hud-luck-read"
        aria-label="luck of the draw"
      >
        <div class="worked-heading">Luck of the draw</div>
        <p class="luck-headline" aria-label="luckHeadline">{{ luckRead.headline }}</p>
        <p class="luck-detail">{{ luckRead.detail }}</p>
        <div class="luck-deltas" aria-label="luck adversity">
          <span v-for="delta in luckRead.deltas" :key="delta.label" class="luck-delta">
            {{ delta.label }}: <strong>{{ delta.actual }}</strong> vs {{ delta.expected }} expected
          </span>
        </div>
      </div>

      <!-- why: WP-595 — the Legendary-Pass AI coach. Pass holders get on-demand
           coaching (hero fit / purchases / tips); non-Pass holders get a locked-
           teaser upsell. Rendered only when the scored record carries a replay
           hash (the coach endpoint's key); the panel resolves Pass status itself. -->
      <EndgameCoachPanel
        v-if="competitiveScore && competitiveScore.replayHash"
        :replay-hash="competitiveScore.replayHash"
      />
    </section>

    <!-- why: guest conversion prompt shown in the (otherwise empty) competitive-
         score slot — only when the viewer has NO competitive score AND played as a
         guest. Forward-looking copy on purpose: a guest cannot save THIS match
         (seat→account ownership is fixed at join time, D-24119), and a guest owns
         no replay (D-24120), so the copy promises only what signing in gets on
         FUTURE matches (saved results, the gauntlet report card, Pass coaching). -->
    <section
      v-if="showGuestSignIn && !competitiveScore"
      class="guest-score-prompt"
      data-testid="arena-hud-guest-sign-in"
      aria-label="sign in to save your results"
    >
      <p class="guest-score-prompt-headline">Sign in to save your results</p>
      <p class="guest-score-prompt-detail">
        You played this match as a guest, so it wasn’t saved. Signed-in matches keep your results, ranked-gauntlet loadouts earn a full score report card, and Legendary Pass holders get AI coaching on every match played to the end.
      </p>
      <!-- why: `?route=login` is the app's sign-in surface (App.vue, D-16008); an
           explicit route=login always wins the route resolution, so the link works
           from the play surface even with `?match=` still in the URL. A plain
           anchor (full navigation) is fine here — the match is already over. -->
      <a class="guest-score-prompt-cta" href="?route=login">Sign in</a>
    </section>

    <!-- why: D-24630 — an expired sign-in on an account-bound seat, NOT a guest
         seat: the match is still savable, so the copy says so instead of "you
         played as a guest". `returnTo=live` makes the LoginPage send the player
         back to this match (via the sessionStorage pending-submit marker, which
         keeps the seat credentials out of the login URL), where the submit fires
         once the session hydrates. Reuses the guest prompt's styling. -->
    <section
      v-if="showSessionExpiredSignIn && !competitiveScore"
      class="guest-score-prompt"
      data-testid="arena-hud-session-expired-sign-in"
      aria-label="sign in to save this match"
    >
      <p class="guest-score-prompt-headline">Your sign-in expired</p>
      <p class="guest-score-prompt-detail">
        Sign in to save this match. You’ll come straight back here and your result will be submitted.
      </p>
      <a class="guest-score-prompt-cta" href="?route=login&amp;returnTo=live">Sign in to save this match</a>
    </section>

    <dl v-if="hasPar && gameOver.par" class="par-breakdown">
      <div class="par-field">
        <dt>Raw score</dt>
        <dd aria-label="rawScore">{{ gameOver.par.rawScore }}</dd>
      </div>
      <div class="par-field">
        <dt>PAR score</dt>
        <dd aria-label="parScore">{{ gameOver.par.parScore }}</dd>
      </div>
      <div class="par-field">
        <dt>Final score</dt>
        <dd aria-label="finalScore">{{ gameOver.par.finalScore }}</dd>
      </div>
      <div class="par-field">
        <dt>Scoring config version</dt>
        <dd aria-label="scoringConfigVersion">
          {{ gameOver.par.scoringConfigVersion }}
        </dd>
      </div>
    </dl>

    <div v-if="hasScores && gameOver.scores" class="scores" data-testid="arena-hud-scores">
      <!-- why: WP-636 — a co-op VP recap for a viewer with NO competitive score
           (a guest, or any non-scored match). The per-player VP is on
           gameOver.scores (NOT competitiveScore), so a guest already has it — the
           old bare count line threw it away, leaving a guest with only the sign-in
           prompt. An account holder's richer per-player block (workedCalc.perPlayer,
           above) already covers this, so render the recap ONLY when competitiveScore
           is absent — otherwise it duplicates. Co-op framing (§23(b)): individual VP
           contribution only, no winner/loser between teammates. -->
      <template v-if="!competitiveScore">
        <p class="scores-heading">Final result — victory points by player</p>
        <!-- why: WP-715 — the neutral cross-seat Table Total, shown on casual matches too
             (sourced from the always-available gameOver.synergyContributions, not the ranked
             workedCalc). Same copy as the ranked headline; omitted when no seat has synergy. -->
        <div
          v-if="coopSynergyTableTotal !== null"
          class="worked-synergy-total"
          data-testid="arena-hud-coop-synergy-total"
        >The table assembled {{ coopSynergyTableTotal }} synergy {{ coopSynergyTableTotal === 1 ? 'clause' : 'clauses' }}</div>
        <ul class="coop-scores" data-testid="arena-hud-coop-scores">
          <li
            v-for="player in gameOver.scores.players"
            :key="player.playerId"
            class="coop-score-row"
            :data-testid="'arena-hud-coop-score-' + player.playerId"
          >
            <span class="coop-score-name">{{ seatLabel(player.playerId) }}</span>
            <strong class="coop-score-total" :aria-label="'player ' + player.playerId + ' total VP'">
              {{ player.totalVP }} VP
            </strong>
            <span class="coop-score-detail">
              villains {{ player.villainVP }} · henchmen {{ player.henchmanVP }} ·
              bystanders {{ player.bystanderVP }} · tactics {{ player.tacticVP }}
            </span>
            <!-- why: WP-715 — this seat's synergy line ("assembled N of M synergy clauses")
                 + Realized Value % on casual matches, delegating to the ranked helpers for
                 identical wording; omitted when the seat has no synergy data. -->
            <span
              v-if="coopSynergyPhrase(gameOver.synergyContributions?.[player.playerId]) !== null"
              class="coop-score-detail coop-score-synergy"
              :data-testid="'arena-hud-coop-synergy-' + player.playerId"
            >{{ coopSynergyPhrase(gameOver.synergyContributions?.[player.playerId]) }}</span>
            <span
              v-if="coopRealizedValuePhrase(gameOver.synergyContributions?.[player.playerId]) !== null"
              class="coop-score-detail coop-score-realized-value"
              :data-testid="'arena-hud-coop-realized-value-' + player.playerId"
            >{{ coopRealizedValuePhrase(gameOver.synergyContributions?.[player.playerId]) }}</span>
          </li>
        </ul>
      </template>
      <p v-else class="scores-note">
        Final scores recorded ({{ gameOver.scores.players.length }} players).
      </p>
    </div>

    <!-- why: WP-752 / D-24576 — the AI coach for a casual (unscored) match, keyed by
         match id (the client has no replay hash without a score record). A SIBLING
         after the recap div, not inside it, so it still renders when gameOver.scores
         is absent. The panel resolves Pass status itself: Pass holders get coaching,
         everyone else the existing teaser. -->
    <EndgameCoachPanel
      v-if="!competitiveScore && casualCoachMatchId !== null"
      :match-id="casualCoachMatchId"
    />
  </section>
</template>

<style scoped>
.endgame-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid var(--color-foreground);
}

header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.outcome {
  font-weight: 700;
  font-size: 1.2rem;
}

.competitive-score {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.competitive-score-headline {
  margin: 0;
  font-size: 1.05rem;
}

.competitive-score-headline strong {
  font-variant-numeric: tabular-nums;
}

.competitive-score-hint {
  font-size: 0.8rem;
  opacity: 0.75;
}

.competitive-score-detail {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
}

/* why: the guest sign-in prompt occupies the competitive-score slot for a guest.
   A bordered card (accent tint) so it reads as a real panel rather than dead
   space, with a clear CTA button. */
.guest-score-prompt {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid color-mix(in srgb, var(--color-foreground) 20%, transparent);
  border-left: 4px solid #0a6bcb;
  border-radius: 8px;
  background: color-mix(in srgb, #0a6bcb 8%, transparent);
}

.guest-score-prompt-headline {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.guest-score-prompt-detail {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.85;
  line-height: 1.45;
}

.guest-score-prompt-cta {
  display: inline-block;
  margin-top: 0.15rem;
  padding: 0.35rem 0.9rem;
  border-radius: 0.4rem;
  background: #0a6bcb;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
}

.guest-score-prompt-cta:hover {
  background: #0959a8;
}

/* why: WP-593 — the raw score as a two-sided ledger. Two columns (penalties |
   earned) on a shared grid so the amounts align, with a full-width net row below.
   Colour reinforces sign (penalties warm, earned cool) but each side is also
   labelled, so the meaning survives with colours disabled. */
.raw-ledger {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.8rem;
  margin: 0.35rem 0 0.1rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid color-mix(in srgb, var(--color-foreground) 18%, transparent);
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
}

.raw-ledger-side {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  padding: 0.4rem 0.5rem;
  border-radius: 8px;
  border-top: 3px solid var(--ledger-color, #888);
  background: color-mix(in srgb, var(--ledger-color, #888) 8%, transparent);
}

/* why: WP-600 — tint each side of the ledger to its sign (penalties warm, earned
   cool), matching the scoring key, so the two-sided math reads at a glance. */
.raw-ledger-penalties { --ledger-color: #bc4c00; }
.raw-ledger-earned { --ledger-color: #1a7f37; }

.raw-ledger-heading {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
  color: var(--ledger-color, var(--color-foreground));
  margin-bottom: 0.1rem;
}

.raw-ledger-sign {
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.75;
  font-size: 0.68rem;
}

.raw-ledger-row {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  font-size: 0.82rem;
  line-height: 1.5;
}

.raw-ledger-label {
  min-width: 0;
  overflow-wrap: anywhere;
  opacity: 0.9;
}

.raw-ledger-amount {
  font-weight: 600;
  white-space: nowrap;
}

.raw-ledger-none .raw-ledger-label,
.raw-ledger-none .raw-ledger-amount {
  opacity: 0.55;
  font-weight: 400;
}

.raw-ledger-penalties .raw-ledger-amount {
  color: #bc4c00;
}

.raw-ledger-earned .raw-ledger-amount {
  color: #1a7f37;
}

.raw-ledger-subtotal {
  margin-top: 0.1rem;
  padding-top: 0.15rem;
  border-top: 1px solid color-mix(in srgb, var(--color-foreground) 14%, transparent);
  font-weight: 700;
}

.raw-ledger-subtotal .raw-ledger-label {
  opacity: 0.75;
  font-weight: 600;
}

.raw-ledger-net {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 0.35rem;
  border-top: 2px solid color-mix(in srgb, var(--color-foreground) 22%, transparent);
}

.raw-ledger-net-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
}

.raw-ledger-net-value {
  font-size: 1.15rem;
  font-weight: 700;
}

.par-breakdown {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 1rem;
  margin: 0.25rem 0 0;
}

.par-field {
  display: flex;
  justify-content: space-between;
}

dt,
dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}

/* why: WP-584 — the score rendered as a worked solution. The formula lines use a
   monospace face + tabular numerals so the arithmetic columns line up, the way a
   marked-up calculation reads. */
.score-worked {
  margin: 0.25rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.worked-givens {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.9rem;
  font-size: 0.8rem;
  color: var(--color-foreground);
  opacity: 0.85;
}

.worked-given strong {
  font-variant-numeric: tabular-nums;
}

.worked-heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.6;
  margin-bottom: 0.15rem;
}

.worked-line {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.85rem;
  line-height: 1.7;
  font-variant-numeric: tabular-nums;
  white-space: normal;
  overflow-wrap: anywhere;
}

.worked-indent {
  padding-left: 1.6rem;
}

.worked-result {
  font-weight: 700;
}

/* why: WP-588 — the per-player split of the reward terms. A display-only
   reconciliation of the team totals (VP + bystanders sum to the raw calc). */
/* why: WP-600 — each seat is its own chip (name on top, VP + rescues below) so the
   per-player split reads as distinct cards rather than an inline run of numbers. */
.per-player-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

.per-player-row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid color-mix(in srgb, var(--color-foreground) 14%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, var(--color-foreground) 4%, transparent);
  font-size: 0.85rem;
  min-width: 8rem;
}

.per-player-name {
  font-weight: 700;
}

.per-player-stat {
  opacity: 0.9;
  font-variant-numeric: tabular-nums;
}

.per-player-stat + .per-player-stat {
  margin-top: 0.05rem;
}

.per-player-stat strong {
  font-variant-numeric: tabular-nums;
}

/* why: WP-621 — the team-contribution line (villains/henchmen/tactics defeated)
   is supporting detail beneath the seat's VP + bystanders, so it reads dimmer
   and smaller than the reward stats above it. */
.per-player-contrib {
  margin-top: 0.2rem;
  opacity: 0.7;
  font-size: 0.78rem;
}

/* why: WP-708 — this seat's celebratory synergy line; a touch brighter than the
   defeated-contribution line above so it reads as a positive callout, not a stat
   footnote (the surface celebrates what was assembled). */
.per-player-synergy {
  margin-top: 0.2rem;
  opacity: 0.85;
  font-size: 0.78rem;
}

/* why: WP-709 — the per-seat Realized Value % line, directly below the synergy
   line; same muted treatment (display-only skill signal, never a scored term). */
.per-player-realized-value {
  margin-top: 0.15rem;
  opacity: 0.85;
  font-size: 0.78rem;
}

/* why: WP-708 — the neutral cross-seat Table Total headline, shown above the
   per-seat rows (the shared "the table assembled N" number). */
.worked-synergy-total {
  margin: 0.15rem 0 0.35rem;
  font-size: 0.82rem;
  opacity: 0.9;
}

/* why: WP-588 — names what sets PAR (the scenario's scheme, mastermind, and
   villain groups). Quiet supporting copy under the PAR derivation. */
.par-basis {
  margin: 0.25rem 0 0;
  font-size: 0.78rem;
  opacity: 0.75;
  font-style: italic;
}

/* why: WP-588 — the grade scale as a horizontal, colour-coded strip (wider, not a
   tall list). Each cell's colour is its grade; the earned cell is emphasised by a
   thicker border + a heavier tint AND a text marker ("your score") + aria-current,
   so colour is reinforcement, never the sole signal. */
.grade-scale {
  margin: 0.4rem 0 0;
}

.grade-scale-strip {
  list-style: none;
  margin: 0.3rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.grade-scale-cell {
  flex: 1 1 4.5rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.08rem;
  padding: 0.35rem 0.2rem 0.3rem;
  border-radius: 6px;
  border: 1px solid transparent;
  border-top: 4px solid var(--grade-color, #888);
  background: color-mix(in srgb, var(--grade-color, #888) 12%, transparent);
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.grade-scale-cell--legendary { --grade-color: #b8860b; }
.grade-scale-cell--a { --grade-color: #1a7f37; }
.grade-scale-cell--b { --grade-color: #0a6bcb; }
.grade-scale-cell--c { --grade-color: #6b7280; }
.grade-scale-cell--d { --grade-color: #bc4c00; }
.grade-scale-cell--f { --grade-color: #cf222e; }

.grade-scale-cell--current {
  border-color: var(--grade-color, #888);
  border-width: 2px;
  border-top-width: 4px;
  background: color-mix(in srgb, var(--grade-color, #888) 26%, transparent);
  font-weight: 700;
}

.grade-scale-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--grade-color, var(--color-foreground));
}

.grade-scale-range {
  font-size: 0.72rem;
  opacity: 0.85;
  white-space: nowrap;
}

.grade-scale-you {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
  color: var(--grade-color, var(--color-foreground));
}

/* why: WP-600 — the scoring key. Two colour-coded columns (awards cool-green |
   penalties warm-orange) matching the raw-ledger palette, so "what things are worth"
   reads at a glance. Colour is reinforcement — each column is also labelled and each
   row leads with its signed points, legible with colours disabled. */
.scoring-key {
  margin: 0.5rem 0 0;
}

.scoring-key-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.3rem;
}

.scoring-key-col {
  border: 1px solid var(--key-color, #888);
  border-top: 3px solid var(--key-color, #888);
  border-radius: 8px;
  padding: 0.4rem 0.55rem 0.5rem;
  background: color-mix(in srgb, var(--key-color, #888) 8%, transparent);
  min-width: 0;
}

.scoring-key-col--awards { --key-color: #1a7f37; }
.scoring-key-col--penalties { --key-color: #bc4c00; }

.scoring-key-col-heading {
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--key-color, var(--color-foreground));
  margin-bottom: 0.3rem;
}

.scoring-key-sign {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  font-size: 0.68rem;
  opacity: 0.8;
}

.scoring-key-row {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  padding: 0.2rem 0;
}

.scoring-key-row + .scoring-key-row {
  border-top: 1px solid color-mix(in srgb, var(--key-color, #888) 16%, transparent);
}

.scoring-key-points {
  flex: 0 0 auto;
  min-width: 2.4rem;
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--key-color, var(--color-foreground));
}

.scoring-key-body {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
}

.scoring-key-label {
  font-size: 0.84rem;
  font-weight: 600;
}

.scoring-key-note {
  font-size: 0.72rem;
  opacity: 0.8;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

/* why: WP-600 — narrow screens stack the two key columns so the notes stay readable. */
@media (max-width: 30rem) {
  .scoring-key-cols {
    grid-template-columns: 1fr;
  }
}

/* why: WP-593 — the luck-of-the-draw read. A tinted card whose accent conveys the
   verdict (favorable cool-green, difficult warm-orange, even neutral), reinforced
   by the headline text so it reads without colour. */
.luck-read {
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--luck-color, #888);
  border-left: 4px solid var(--luck-color, #888);
  background: color-mix(in srgb, var(--luck-color, #888) 10%, transparent);
}

.luck-read--favorable { --luck-color: #1a7f37; }
.luck-read--average { --luck-color: #6b7280; }
.luck-read--difficult { --luck-color: #bc4c00; }

.luck-headline {
  margin: 0.1rem 0 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--luck-color, var(--color-foreground));
}

.luck-detail {
  margin: 0.15rem 0 0;
  font-size: 0.82rem;
  opacity: 0.9;
  line-height: 1.45;
}

.luck-deltas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.9rem;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
}

.luck-delta strong {
  font-variant-numeric: tabular-nums;
}

/* why: WP-583 — the grade badge. Meaning is carried by the text label; the
   background tint is decorative reinforcement only (never the sole signal),
   so the badge stays legible with colours disabled or to a screen reader. */
.grade-badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.25rem;
  font-weight: 700;
  font-size: 0.85rem;
  line-height: 1.4;
  border: 1px solid currentColor;
}

.grade-badge--legendary {
  color: #b8860b;
  background: rgba(255, 215, 0, 0.16);
}

.grade-badge--a {
  color: #1a7f37;
  background: rgba(35, 197, 98, 0.16);
}

.grade-badge--b {
  color: #0a6bcb;
  background: rgba(31, 136, 219, 0.16);
}

.grade-badge--c {
  color: var(--color-foreground);
  background: rgba(128, 128, 128, 0.16);
}

.grade-badge--d {
  color: #bc4c00;
  background: rgba(219, 109, 40, 0.16);
}

.grade-badge--f {
  color: #cf222e;
  background: rgba(207, 34, 46, 0.16);
}

/* why: WP-636 — the guest / non-scored co-op VP recap. Compact stacked rows so it
   reads in the narrow endgame panel without a wide table overflowing. */
.scores-heading {
  margin: 0 0 0.25rem;
  font-weight: 600;
}

.coop-scores {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.coop-score-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
}

.coop-score-name {
  font-weight: 600;
}

.coop-score-total {
  font-variant-numeric: tabular-nums;
}

.coop-score-detail {
  flex-basis: 100%;
  font-size: 0.85em;
  opacity: 0.8;
}
</style>
